<i18n locale="en">
{
  "chat-cta": "Questions? (111) 222-3333",
  "price-period": "/ month",
  "upgrade-cta": "Upgrade to Max"
}
</i18n>
<template>
  <div
    class="upsell-hero flex flex-col items-start justify-between gap-8"
    data-testid="upsell-hero"
  >
    <div class="flex flex-col items-start gap-4">
      <UpsellGateBadge :text="badgeText" />
      <OwnType
        :text="title"
        :variant="compact ? 'title-regular' : 'heading-small'"
      />
      <OwnType
        v-if="description"
        :text="description"
        variant="paragraph-small"
        color="secondary"
      />
    </div>
    <div class="flex w-full flex-col gap-[18px]">
      <div v-if="price" class="flex items-baseline gap-1">
        <OwnType :text="price" variant="heading-small" />
        <OwnType
          :text="pricePeriod ?? t('price-period')"
          variant="paragraph-very-small"
          color="secondary"
        />
      </div>
      <div class="upsell-hero__actions flex w-full flex-col gap-2">
        <OwnButton
          class="upsell-hero__upgrade-button"
          :small="compact"
          :text="upgradeText ?? t('upgrade-cta')"
          variant="primary"
          data-testid="upsell-hero-upgrade-button"
          @click="emit('upgrade')"
        />
        <OwnButton
          class="upsell-hero__chat-button"
          :small="compact"
          :text="chatText ?? t('chat-cta')"
          data-testid="upsell-hero-chat-button"
          @click="emit('chat')"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n'

import { OwnButton } from '@/ui/OwnButton'
import { OwnType } from '@/ui/OwnType'

import UpsellGateBadge from './UpsellGateBadge.vue'

withDefaults(
  defineProps<{
    badgeText?: string
    chatText?: string
    /** Smaller type and buttons for tight surfaces like the upsell modal. */
    compact?: boolean
    description?: string
    price?: string
    pricePeriod?: string
    title: string
    upgradeText?: string
  }>(),
  {
    badgeText: undefined,
    chatText: undefined,
    compact: false,
    description: undefined,
    price: undefined,
    pricePeriod: undefined,
    upgradeText: undefined,
  }
)

const emit = defineEmits<{
  chat: []
  upgrade: []
}>()

const { t } = useI18n()
</script>

<style lang="scss" scoped>
// The tier-upgrades design uses a black primary CTA and a quiet filled
// secondary, diverging from the default OwnButton palette.
.upsell-hero {
  // Design buttons use 14px (text size s) labels; OwnButton hardcodes its
  // label to text-button-regular (16px).
  &__actions :deep(.text-button-regular) {
    font-size: 0.875rem;
    line-height: 1.125rem;
  }

  .upsell-hero__upgrade-button {
    background: var(--text-color-primary);
    box-shadow: var(--shadow-card-default);

    &:hover {
      background-color: #2c2c2c;
    }
  }

  .upsell-hero__chat-button {
    color: var(--text-color-secondary);
    background-color: var(--background-quarternary);
    box-shadow: none;

    &:hover {
      background-color: var(--background-secondary);
    }
  }
}
</style>
